import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Book, LogOut, Settings, Calendar, Image as ImageIcon, Wand2, Filter, ChevronDown, ChevronUp, Link2, FileArchive, Package, Upload } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import UserSettings from '../components/UserSettings';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [shoeboxes, setShoeboxes] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shoeboxesCollapsed, setShoeboxesCollapsed] = useState(false);
  const [albumsCollapsed, setAlbumsCollapsed] = useState(false);
  const [defaultAiInstructions, setDefaultAiInstructions] = useState(`1. Search for significant life events, people and places.
2. If many images are taken at the same scene, select the best images for clarity, smiles and eyes open in the image.
3. Skip MEMES and screenshots.
4. Try to pick out significant images and stories that are worthy of a photoalbum/keepsake.
5. Focus on images with emotional value and memorable moments.`);
  const [newAlbum, setNewAlbum] = useState({
    title: '',
    description: '',
    year: new Date().getFullYear(),
    ai_instructions: '',
    filters: {
      start_date: '',
      end_date: '',
      location_keyword: '',
      person_keyword: '',
      min_people: null
    }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [zipUploading, setZipUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
    fetchAlbums();
    fetchShoeboxes();
    fetchDefaultAiInstructions();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to load user data');
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data);
    } catch (error) {
      toast.error('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  const fetchShoeboxes = async () => {
    try {
      const response = await axios.get(`${API}/shoeboxes`);
      setShoeboxes(response.data);
    } catch (error) {
      console.error('Failed to load shoeboxes');
    }
  };

  const fetchDefaultAiInstructions = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.default_ai_instructions) {
        setDefaultAiInstructions(response.data.default_ai_instructions);
        setNewAlbum(prev => ({ ...prev, ai_instructions: response.data.default_ai_instructions }));
      }
    } catch (error) {
      console.error('Failed to load default AI instructions:', error);
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/albums`, newAlbum);
      toast.success('Album created successfully!');
      setAlbums([response.data, ...albums]);
      setShowCreateDialog(false);
      setNewAlbum({ 
        title: '', 
        description: '', 
        year: new Date().getFullYear(),
        ai_instructions: defaultAiInstructions,
        filters: {
          start_date: '',
          end_date: '',
          location_keyword: '',
          person_keyword: '',
          min_people: null
        }
      });
      setShowFilters(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create album');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setPhotoUploading(true);
    setPhotoUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await axios.post(`${API}/library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPhotoUploadProgress(progress);
        }
      });

      toast.success(`Uploaded ${response.data.uploaded_count} photos!`);
      fetchShoeboxes();
      setPhotoUploadProgress(0);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setZipUploading(true);
    setZipUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/library/upload-zip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setZipUploadProgress(progress);
        }
      });

      toast.success(`Extracted and uploaded ${response.data.extracted_count} photos!`);
      fetchShoeboxes();
      setZipUploadProgress(0);
    } catch (error) {
      toast.error('Failed to process ZIP file');
    } finally {
      setZipUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Your Albums</h2>
            <p className="text-gray-600">Create and manage your photo collections</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/library')}
              variant="outline"
              size="lg"
              className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <ImageIcon className="w-5 h-5 mr-2" />
              Photo Library
            </Button>
            <Button
              data-testid="create-album-btn"
              onClick={() => {
                if (user?.subscription_tier === 'free' && albums.length >= 4) {
                  toast.error('Free tier limited to 4 albums. Upgrade to create unlimited albums!', {
                    duration: 5000
                  });
                  return;
                }
                setShowCreateDialog(true);
              }}
              className="bg-gradient-to-r from-amber-600 to-rose-500 text-white"
              size="lg"
              disabled={user?.subscription_tier === 'free' && albums.length >= 4}
            >
              <Plus className="w-5 h-5 mr-2" />
              {user?.subscription_tier === 'free' && albums.length >= 4 ? 'Upgrade to Create More' : 'Create Album'}
            </Button>
          </div>
        </div>

        {/* Shoeboxes Section - SHOWN FIRST */}
        <div className="mb-8">
          <div 
            className="flex items-center justify-between mb-6 cursor-pointer bg-white/60 backdrop-blur-sm rounded-xl p-4 border-2 border-gray-200 hover:border-blue-400 transition-colors"
            onClick={() => setShoeboxesCollapsed(!shoeboxesCollapsed)}
          >
            <div className="flex items-center gap-3">
              <Package className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Your Shoeboxes</h2>
                <p className="text-sm text-gray-600">Uploaded photo collections ({shoeboxes.length})</p>
              </div>
            </div>
            <Button variant="ghost" size="sm">
              {shoeboxesCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </Button>
          </div>

          {!shoeboxesCollapsed && (
            <>
              {/* Import Photos Section - Always Show */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-dashed border-gray-300">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Import Photos to Library</h3>
                <p className="text-gray-600 text-sm mb-4">Upload photos to automatically create shoeboxes</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Upload Photos */}
                  <button
                    onClick={() => !photoUploading && document.getElementById('dashboard-photo-upload').click()}
                    disabled={photoUploading}
                    className="relative flex flex-col items-center justify-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all disabled:opacity-75"
                  >
                    {photoUploading ? (
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Uploading...</span>
                          <span className="text-sm font-bold text-blue-600">{photoUploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${photoUploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-blue-600 mb-2" />
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">Upload Photos</h4>
                        <p className="text-xs text-gray-600 text-center">Select multiple images</p>
                      </>
                    )}
                  </button>

                  {/* Upload ZIP */}
                  <button
                    onClick={() => !zipUploading && document.getElementById('dashboard-zip-upload').click()}
                    disabled={zipUploading}
                    className="relative flex flex-col items-center justify-center p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all disabled:opacity-75"
                  >
                    {zipUploading ? (
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {zipUploadProgress === 100 ? 'Processing...' : 'Uploading...'}
                          </span>
                          <span className="text-sm font-bold text-purple-600">{zipUploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${zipUploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <FileArchive className="w-10 h-10 text-purple-600 mb-2" />
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">Upload ZIP</h4>
                        <p className="text-xs text-gray-600 text-center">Upload entire folder</p>
                      </>
                    )}
                  </button>

                  {/* Link Accounts */}
                  <button
                    onClick={() => navigate('/connected-accounts')}
                    disabled={photoUploading || zipUploading}
                    className="flex flex-col items-center justify-center p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all disabled:opacity-50"
                  >
                    <Link2 className="w-10 h-10 text-green-600 mb-2" />
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Link Accounts</h4>
                    <p className="text-xs text-gray-600 text-center">Connect cloud services</p>
                  </button>
                </div>

                {/* Hidden file inputs */}
                <input
                  id="dashboard-photo-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <input
                  id="dashboard-zip-upload"
                  type="file"
                  accept=".zip"
                  onChange={handleZipUpload}
                  className="hidden"
                />
              </div>

              {/* Shoeboxes Grid */}
              {shoeboxes.length === 0 ? (
                <div className="text-center py-8 bg-white/50 rounded-2xl border border-gray-200">
                  <p className="text-gray-500 text-sm">No shoeboxes yet. Upload photos above to get started!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {shoeboxes.map((shoebox) => (
                    <ShoeboxCard 
                      key={shoebox.id} 
                      shoebox={shoebox} 
                      onClick={() => navigate(`/shoebox/${shoebox.id}`)} 
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Albums Section - SHOWN SECOND */}
        <div className="mb-8">
          <div 
            className="flex items-center justify-between mb-6 cursor-pointer bg-white/60 backdrop-blur-sm rounded-xl p-4 border-2 border-gray-200 hover:border-amber-400 transition-colors"
            onClick={() => setAlbumsCollapsed(!albumsCollapsed)}
          >
            <div className="flex items-center gap-3">
              <Book className="w-6 h-6 text-amber-600" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-gray-900">My Albums</h2>
                  {user?.subscription_tier === 'free' && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      {albums.length}/4
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  Curated photo collections ({albums.length})
                  {user?.subscription_tier === 'free' && albums.length >= 4 && (
                    <span className="text-amber-600 font-semibold ml-2">• Limit reached</span>
                  )}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm">
              {albumsCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </Button>
          </div>

          {!albumsCollapsed && (
            <>
              {albums.length === 0 ? (
                <div data-testid="empty-albums-state" className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-gray-300">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center mx-auto mb-4">
                    <Book className="w-10 h-10 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No albums yet</h3>
                  <p className="text-gray-500 mb-6">
                    Create your first photo album to organize and preserve your favorite memories
                  </p>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-gradient-to-r from-amber-600 to-rose-500 text-white shadow-lg hover:shadow-xl"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Your First Album
                  </Button>
                  <p className="text-xs text-gray-500 mt-4">
                    ✨ Select photos from your library and let AI help curate
                  </p>
                </div>
              ) : (
                <div className="album-grid">
                  {albums.map((album) => (
                    <AlbumCard key={album.id} album={album} onClick={() => navigate(`/album/${album.id}`)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* User Settings Dialog */}
      <UserSettings open={showSettings} onOpenChange={setShowSettings} />

      {/* Create Album Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-album-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Album</DialogTitle>
            <DialogDescription>Create a new photo album to organize your memories</DialogDescription>
          </DialogHeader>
          <form data-testid="create-album-form" onSubmit={handleCreateAlbum} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Album Title</Label>
              <Input
                data-testid="album-title-input"
                id="title"
                placeholder="Summer Vacation 2024"
                value={newAlbum.title}
                onChange={(e) => setNewAlbum({ ...newAlbum, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                data-testid="album-description-input"
                id="description"
                placeholder="A collection of memories from our amazing summer trip..."
                value={newAlbum.description}
                onChange={(e) => setNewAlbum({ ...newAlbum, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input
                data-testid="album-year-input"
                id="year"
                type="number"
                placeholder="2024"
                value={newAlbum.year}
                onChange={(e) => setNewAlbum({ ...newAlbum, year: parseInt(e.target.value) })}
                min="1900"
                max="2100"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-instructions" className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                AI Instructions
              </Label>
              <Textarea
                data-testid="album-ai-instructions"
                id="ai-instructions"
                placeholder="Instructions for AI photo analysis..."
                value={newAlbum.ai_instructions}
                onChange={(e) => setNewAlbum({ ...newAlbum, ai_instructions: e.target.value })}
                rows={5}
                className="text-sm"
              />
              <p className="text-xs text-gray-500">
                These instructions guide AI when analyzing and selecting photos for this album. You can customize them or use the defaults.
              </p>
            </div>

            {/* Filter Toggle */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters (Optional)
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showFilters && (
                <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-3">
                    Set criteria to automatically filter photos when uploading to this album
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="start-date" className="text-xs">Start Date</Label>
                      <Input
                        data-testid="filter-start-date"
                        id="start-date"
                        type="date"
                        value={newAlbum.filters.start_date}
                        onChange={(e) => setNewAlbum({ 
                          ...newAlbum, 
                          filters: { ...newAlbum.filters, start_date: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-date" className="text-xs">End Date</Label>
                      <Input
                        data-testid="filter-end-date"
                        id="end-date"
                        type="date"
                        value={newAlbum.filters.end_date}
                        onChange={(e) => setNewAlbum({ 
                          ...newAlbum, 
                          filters: { ...newAlbum.filters, end_date: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="location-filter" className="text-xs">Location Contains</Label>
                    <Input
                      data-testid="filter-location"
                      id="location-filter"
                      placeholder="e.g., Paris, Beach, Home"
                      value={newAlbum.filters.location_keyword}
                      onChange={(e) => setNewAlbum({ 
                        ...newAlbum, 
                        filters: { ...newAlbum.filters, location_keyword: e.target.value }
                      })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="person-filter" className="text-xs">Person Name</Label>
                    <Input
                      data-testid="filter-person"
                      id="person-filter"
                      placeholder="e.g., John, Sarah"
                      value={newAlbum.filters.person_keyword}
                      onChange={(e) => setNewAlbum({ 
                        ...newAlbum, 
                        filters: { ...newAlbum.filters, person_keyword: e.target.value }
                      })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="min-people" className="text-xs">Minimum People in Photo</Label>
                    <Input
                      data-testid="filter-min-people"
                      id="min-people"
                      type="number"
                      placeholder="e.g., 2"
                      min="0"
                      value={newAlbum.filters.min_people || ''}
                      onChange={(e) => setNewAlbum({ 
                        ...newAlbum, 
                        filters: { ...newAlbum.filters, min_people: e.target.value ? parseInt(e.target.value) : null }
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
            <Button
              data-testid="create-album-submit-btn"
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-rose-500 text-white"
            >
              Create Album
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AlbumCard = ({ album, onClick }) => (
  <div
    data-testid={`album-card-${album.id}`}
    onClick={onClick}
    className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-amber-400 hover:shadow-xl cursor-pointer group transition-all duration-300 transform hover:-translate-y-1"
  >
    {/* Album Cover/Thumbnail Area */}
    <div className="relative h-48 bg-gradient-to-br from-amber-100 via-rose-100 to-blue-100">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-r from-amber-400 to-rose-400 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
          <ImageIcon className="w-10 h-10" />
        </div>
      </div>
      {/* Photo count badge */}
      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
        <span className="text-sm font-semibold text-gray-700">{album.photo_count} {album.photo_count === 1 ? 'photo' : 'photos'}</span>
      </div>
      {/* Year badge */}
      {album.year && (
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md flex items-center gap-1">
          <Calendar className="w-3 h-3 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">{album.year}</span>
        </div>
      )}
      {/* Public badge */}
      {album.is_public && (
        <div className="absolute bottom-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full shadow-md">
          <span className="text-xs font-semibold">Public</span>
        </div>
      )}
    </div>
    
    {/* Album Info */}
    <div className="p-5">
      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-1">
        {album.title}
      </h3>
      {album.description ? (
        <p className="text-gray-600 text-sm mb-3 line-clamp-2 min-h-[40px]">{album.description}</p>
      ) : (
        <p className="text-gray-400 text-sm italic mb-3 min-h-[40px]">No description</p>
      )}
      
      {/* Empty album call to action */}
      {album.photo_count === 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
            <Plus className="w-3 h-3" />
            Click to add photos
          </p>
        </div>
      )}
    </div>
  </div>
);

const ShoeboxCard = ({ shoebox, onClick }) => {
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg cursor-pointer group transition-all duration-300"
    >
      {/* Shoebox Header */}
      <div className="relative h-32 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform">
            <Package className="w-8 h-8" />
          </div>
        </div>
        {/* Photo count badge */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
          <span className="text-sm font-semibold text-gray-700">{shoebox.photo_count} photos</span>
        </div>
      </div>

      {/* Shoebox Info */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-1">
          {shoebox.name}
        </h3>
        <p className="text-xs text-gray-500">
          {formatDate(shoebox.created_at)}
        </p>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-blue-600 font-medium flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            Click to view & manage photos
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
